/**
 * The durable photo delivery queue (M5 / S05-03) — the thing Risk Probe C
 * deliberately did NOT prove, built and provable.
 *
 * ORDER, PER D-025 / D-028, per photo:
 *
 *   display → thumbnail → attach_capture_photo → archive
 *          → confirm_capture_photo_archive_uploaded → local cleanup
 *
 * Derivatives first, so the photo becomes useful quickly. The row exists once
 * the optimized variants are durable, with `archive_uploaded_at` NULL until the
 * SERVER confirms the archive — nothing here fakes that, and local bytes are
 * removed only after the confirmation round-trips.
 *
 * EVERY TRANSITION IS PERSISTED (with readback) BEFORE THE NEXT NETWORK CALL,
 * so a process death at any point resumes at exactly the completed stage. The
 * queue is rebuilt from IndexedDB, never from in-memory state.
 *
 * SEQUENTIAL BY DESIGN (R1 F-3 of S05-02, recorded in I-042): one photo at a
 * time, one stage at a time. Probe C measured 48.5 kB/s on a weak field
 * signal, and nothing is known about decoding or uploading twelve 12-megapixel
 * photos concurrently on a mid-range phone — so nothing does.
 *
 * FAILURE IS A STATE, NEVER A LIE. A failed stage records `lastFailure`,
 * leaves the stage where it was, and moves on to the NEXT photo — one photo's
 * bad luck must not strand the others. A retry re-enters at the failed stage
 * with the same `client_photo_uuid` and the same object paths, so Storage
 * upserts replace rather than duplicate, attach's idempotency returns the
 * existing row, and confirmation's idempotency returns the existing timestamp.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { PhotoStagingStore, StagedPhoto } from './photo-staging';
import { deliveryStageIndex } from './photo-staging';
import { normalizeExifOrientation } from './photo-bytes';

export const PHOTO_BUCKET = 'capture-photos';

/** D-026, verbatim. */
/**
 * **THERE IS NO FIXED PHOTO-COUNT CEILING (H-4, superseding D-026's 12).**
 *
 * Construction documentation legitimately needs more than twelve photos, and a
 * round number was never a resource limit — it was a guess standing in for
 * one. What actually costs something is bytes, and those are still bounded:
 * 25 MB per source photo and 100 MB of staged bytes per device, both unchanged
 * and both enforced below. A refusal now names the real constraint instead of
 * an arbitrary count.
 */
export const MAX_STAGED_SOURCE_BYTES = 104_857_600; // 100 × 1024 × 1024
export { MAX_SOURCE_BYTES } from './photo-pipeline';

const extensionFor = (mime: 'image/jpeg' | 'image/png'): 'jpg' | 'png' =>
  mime === 'image/png' ? 'png' : 'jpg';

/**
 * EXIF's `2026:08:14 09:30:00` is NOT a timestamptz, and a bare local time
 * without a zone is not an instant. The instant exists only when the device
 * also reported its UTC offset — so the two are composed into ISO-8601 with
 * that offset, and WITHOUT the offset the answer is null. Migration 0006 makes
 * the column nullable for exactly this reason: missing metadata never blocks a
 * photo save, and a guessed timezone would be a fabricated instant.
 */
export function composeDeviceCapturedAt(
  capturedAtRaw: string | null,
  utcOffsetMinutes: number | null,
): string | null {
  if (capturedAtRaw === null || utcOffsetMinutes === null) return null;
  const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(capturedAtRaw.trim());
  if (!match) return null;
  const sign = utcOffsetMinutes < 0 ? '-' : '+';
  const magnitude = Math.abs(utcOffsetMinutes);
  const oh = String(Math.floor(magnitude / 60)).padStart(2, '0');
  const om = String(magnitude % 60).padStart(2, '0');
  return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}${sign}${oh}:${om}`;
}

/**
 * `{owner}/{capture}/{client_photo_uuid}/{variant}.{ext}` — the shape the
 * storage policies key on and `confirm_capture_photo_archive_uploaded`
 * derives independently from row truth. These two derivations MUST agree; the
 * hosted verification proved the server side, and tests pin this side.
 */
export function photoObjectPath(
  record: Pick<StagedPhoto, 'ownerUserId' | 'captureId' | 'clientPhotoUuid'>,
  variant: 'archive' | 'display' | 'thumb',
  mime: 'image/jpeg' | 'image/png',
): string {
  return `${record.ownerUserId}/${record.captureId}/${record.clientPhotoUuid}/${variant}.${extensionFor(mime)}`;
}

/* ==========================================================================
 * Transport port — the only place the network exists
 * ========================================================================== */

export type TransportResult<T> = { ok: true; value: T } | { ok: false; failure: string };

export interface AttachArgs {
  readonly captureId: string;
  readonly clientPhotoUuid: string;
  readonly photoClass: 'before' | 'after';
  readonly sourceMime: 'image/jpeg' | 'image/png';
  readonly archiveByteSize: number;
  readonly archiveSha256: string;
  readonly sourceWidth: number;
  readonly sourceHeight: number;
  readonly sourceOrientation: number | null;
  readonly deviceCapturedAtRaw: string | null;
  readonly deviceUtcOffsetMinutes: number | null;
  readonly sanitizerVersion: string;
}

export interface PhotoTransportPort {
  readonly uploadObject: (
    path: string,
    bytes: Uint8Array,
    mime: 'image/jpeg' | 'image/png',
  ) => Promise<TransportResult<null>>;
  readonly attachPhoto: (args: AttachArgs) => Promise<TransportResult<{ photoId: string }>>;
  readonly confirmArchive: (
    photoId: string,
  ) => Promise<TransportResult<{ archiveUploadedAt: string | null }>>;
  /** Active (non-removed) rows on a Capture — the server half of D-026's count. */
  readonly countActivePhotos: (captureId: string) => Promise<TransportResult<number>>;
}

/**
 * The real transport. `client` is a parameter, not an import of the app
 * singleton, so the integration suite can hand it a locally signed-in client
 * and exercise this EXACT code against a real Storage and real RPCs.
 *
 * Owner identity never comes from the caller: the storage policies key on
 * `auth.uid()` and every RPC resolves ownership server-side, so a wrong
 * `ownerUserId` in a path produces a refusal, not a cross-owner write.
 */
export function createSupabasePhotoTransport(client: SupabaseClient): PhotoTransportPort {
  return {
    async uploadObject(path, bytes, mime) {
      try {
        const { error } = await client.storage
          .from(PHOTO_BUCKET)
          .upload(path, new Blob([bytes as BlobPart], { type: mime }), {
            contentType: mime,
            upsert: true, // retry REPLACES the same object — never duplicates
          });
        if (error) return { ok: false, failure: error.message };
        return { ok: true, value: null };
      } catch (error) {
        // A dropped connection lands here, and it must read as FAILED — never
        // as a silent success.
        return { ok: false, failure: error instanceof Error ? error.message : String(error) };
      }
    },

    async attachPhoto(args) {
      try {
        // The generated client types this rpc's payload as `any`; pin it to
        // `unknown` at the boundary and validate the shape before trusting it.
        const { data, error } = (await client.rpc('attach_capture_photo', {
          p_capture_id: args.captureId,
          p_client_photo_uuid: args.clientPhotoUuid,
          p_photo_class: args.photoClass,
          p_source_mime: args.sourceMime,
          p_archive_byte_size: args.archiveByteSize,
          p_archive_sha256: args.archiveSha256,
          p_source_width: args.sourceWidth,
          p_source_height: args.sourceHeight,
          p_source_orientation: args.sourceOrientation,
          p_device_captured_at: composeDeviceCapturedAt(
            args.deviceCapturedAtRaw,
            args.deviceUtcOffsetMinutes,
          ),
          p_device_utc_offset_minutes: args.deviceUtcOffsetMinutes,
          p_sanitizer_version: args.sanitizerVersion,
        })) as { data: unknown; error: { message: string } | null };
        if (error) return { ok: false, failure: error.message };
        const row = data as { id?: unknown } | null;
        if (!row || typeof row.id !== 'string') {
          return { ok: false, failure: 'attach returned no row' };
        }
        return { ok: true, value: { photoId: row.id } };
      } catch (error) {
        return { ok: false, failure: error instanceof Error ? error.message : String(error) };
      }
    },

    async confirmArchive(photoId) {
      try {
        const { data, error } = (await client.rpc('confirm_capture_photo_archive_uploaded', {
          p_photo_id: photoId,
        })) as { data: unknown; error: { message: string } | null };
        if (error) return { ok: false, failure: error.message };
        const row = data as { archive_uploaded_at?: unknown } | null;
        const at =
          row && typeof row.archive_uploaded_at === 'string' ? row.archive_uploaded_at : null;
        return { ok: true, value: { archiveUploadedAt: at } };
      } catch (error) {
        return { ok: false, failure: error instanceof Error ? error.message : String(error) };
      }
    },

    async countActivePhotos(captureId) {
      try {
        const { count, error } = await client
          .from('capture_photo')
          .select('id', { count: 'exact', head: true })
          .eq('capture_id', captureId)
          .is('removed_at', null);
        if (error) return { ok: false, failure: error.message };
        return { ok: true, value: count ?? 0 };
      } catch (error) {
        return { ok: false, failure: error instanceof Error ? error.message : String(error) };
      }
    },
  };
}

/* ==========================================================================
 * D-026 limits — pure, so every branch is directly testable
 * ========================================================================== */

/**
 * `photo-count` is GONE, not merely unused: with no count ceiling nothing can
 * produce it, and a refusal code that can never fire is a trap for the next
 * reader. The two that remain are the real resource limits.
 */
export type LimitRefusalCode = 'photo-too-large' | 'staged-budget';

export interface LimitRefusal {
  readonly ok: false;
  readonly code: LimitRefusalCode;
  readonly reason: string;
}

export type LimitCheck = { readonly ok: true } | LimitRefusal;

/**
 * Evaluated in D-026's precedence, and ONLY that order: working count →
 * individual source size → staged-source-byte budget.
 *
 * The two limits deliberately count DIFFERENT things (D-026):
 *
 *   The COUNT is active server rows plus staged photos that have no row yet —
 *   a staged photo that reached `attached` has a row and is counted by the
 *   server side, never twice. Removed photos are excluded by the transport's
 *   `removed_at is null` filter and consume nothing.
 *
 *   The BUDGET is source bytes still RETAINED LOCALLY — including photos whose
 *   row exists while the archive is unconfirmed, because their bytes really
 *   are still on the device. Only confirmation (which deletes the local
 *   record) releases budget. Actual byte lengths, never estimates.
 */
export function evaluatePhotoLimits(input: {
  readonly staged: readonly StagedPhoto[];
  readonly captureId: string;
  readonly candidateSourceBytes: number;
  readonly maxSourceBytes: number;
}): LimitCheck {
  const { staged, candidateSourceBytes, maxSourceBytes } = input;

  // 1. INDIVIDUAL SOURCE SIZE — same ceiling S05-02 enforces, re-checked here
  // so the boundary holds even if a caller skipped the pipeline's refusal.
  if (candidateSourceBytes > maxSourceBytes) {
    return {
      ok: false,
      code: 'photo-too-large',
      reason: `This photo is larger than the ${String(Math.round(maxSourceBytes / 1_048_576))} MB limit.`,
    };
  }

  // 3. STAGED BUDGET — every owner's retained photo, across Captures, because
  // the device holds them all. Includes attached-but-unconfirmed records.
  const retainedSourceBytes = staged.reduce((sum, record) => sum + record.sourceByteSize, 0);
  if (retainedSourceBytes + candidateSourceBytes > MAX_STAGED_SOURCE_BYTES) {
    return {
      ok: false,
      code: 'staged-budget',
      reason: 'Not enough space to keep this photo until it uploads. Let uploads finish first.',
    };
  }

  return { ok: true };
}

/** Actual retained bytes across ALL variants — reporting truth, PNG included. */
export function totalRetainedBytes(staged: readonly StagedPhoto[]): number {
  return staged.reduce(
    (sum, record) =>
      sum + record.archive.byteSize + record.display.byteSize + record.thumbnail.byteSize,
    0,
  );
}

/* ==========================================================================
 * The queue
 * ========================================================================== */

export interface DeliveryOutcome {
  readonly clientPhotoUuid: string;
  /** Stage after this pass — 'confirmed' means delivered and cleaned up. */
  readonly stage: StagedPhoto['stage'];
  readonly delivered: boolean;
  readonly failure: string | null;
}

/**
 * Advance ONE photo as far as it will go, persisting (with readback) after
 * every completed stage. Returns where it stopped.
 *
 * Each case deliberately FALLS THROUGH to the next stage on success — that is
 * the resume property: a record loaded at 'thumb-uploaded' enters at attach,
 * repeating nothing already completed.
 */
async function advancePhoto(
  staging: PhotoStagingStore,
  transport: PhotoTransportPort,
  input: StagedPhoto,
  epoch: number,
): Promise<DeliveryOutcome> {
  let record = input;

  // R1 B-1: `epoch` is the owner's purge generation captured when THIS
  // delivery pass began. Every persist below carries it, so an explicit
  // sign-out that lands anywhere in this function — during a network await,
  // between a check and a write, anywhere — makes every later persist a
  // refusal instead of a resurrection. The in-flight network request itself
  // is not cancelled (deliberately: the server truth it produces is still
  // true); only LOCAL persistence stops.
  const purged = (): boolean => staging.purgeEpoch(record.ownerUserId) !== epoch;

  const abortSignedOut = (): DeliveryOutcome => ({
    clientPhotoUuid: record.clientPhotoUuid,
    stage: record.stage,
    delivered: false,
    failure: 'signed out during delivery',
  });

  const persist = async (next: StagedPhoto): Promise<StagedPhoto | null> =>
    await staging.update({ ...next, updatedAtMs: Date.now() }, epoch);

  const fail = async (failure: string): Promise<DeliveryOutcome> => {
    // The stage stays where it was — failure never advances or rewinds it.
    // After a purge this write-back is refused by the epoch guard, which is
    // exactly right: a failure note must not resurrect purged bytes either.
    await persist({ ...record, attempts: record.attempts + 1, lastFailure: failure });
    return {
      clientPhotoUuid: record.clientPhotoUuid,
      stage: record.stage,
      delivered: false,
      failure,
    };
  };

  const stageAt = (stage: StagedPhoto['stage']): boolean =>
    deliveryStageIndex(record.stage) < deliveryStageIndex(stage);

  // ---- display -------------------------------------------------------------
  if (stageAt('display-uploaded')) {
    if (purged()) return abortSignedOut();
    const path = photoObjectPath(record, 'display', record.display.mime);
    const result = await transport.uploadObject(path, record.display.bytes, record.display.mime);
    if (!result.ok) return await fail(result.failure);
    const persisted = await persist({ ...record, stage: 'display-uploaded', lastFailure: null });
    if (!persisted)
      return purged() ? abortSignedOut() : await fail('could not persist delivery state');
    record = persisted;
  }

  // ---- thumbnail -----------------------------------------------------------
  if (stageAt('thumb-uploaded')) {
    if (purged()) return abortSignedOut();
    const path = photoObjectPath(record, 'thumb', record.thumbnail.mime);
    const result = await transport.uploadObject(
      path,
      record.thumbnail.bytes,
      record.thumbnail.mime,
    );
    if (!result.ok) return await fail(result.failure);
    const persisted = await persist({ ...record, stage: 'thumb-uploaded', lastFailure: null });
    if (!persisted)
      return purged() ? abortSignedOut() : await fail('could not persist delivery state');
    record = persisted;
  }

  // ---- attach --------------------------------------------------------------
  if (stageAt('attached')) {
    if (purged()) return abortSignedOut();
    const result = await transport.attachPhoto({
      captureId: record.captureId,
      clientPhotoUuid: record.clientPhotoUuid,
      photoClass: record.photoClass,
      sourceMime: record.archive.mime,
      archiveByteSize: record.archive.byteSize,
      archiveSha256: record.archive.sha256,
      sourceWidth: record.archive.width,
      sourceHeight: record.archive.height,
      // THE SECOND APPLICATION, AND THE ONE THAT RESCUES AN ALREADY-STAGED
      // PHOTO. A record written by a build without the reader-side check holds
      // its invalid orientation ON DISK: fixing only the parser would leave
      // that photo retrying the same refused value forever, with its display
      // and thumbnail objects already uploaded and no row to own them. The
      // record is NOT rewritten — the same `client_photo_uuid`, the same
      // paths, the same queue entry — only what crosses to the server is
      // normalized.
      sourceOrientation: normalizeExifOrientation(record.orientation),
      deviceCapturedAtRaw: record.capturedAtRaw,
      deviceUtcOffsetMinutes: record.utcOffsetMinutes,
      sanitizerVersion: record.sanitizerVersion,
    });
    if (!result.ok) return await fail(result.failure);
    const persisted = await persist({
      ...record,
      stage: 'attached',
      serverPhotoId: result.value.photoId,
      lastFailure: null,
    });
    if (!persisted)
      return purged() ? abortSignedOut() : await fail('could not persist delivery state');
    record = persisted;
  }

  // ---- archive -------------------------------------------------------------
  if (stageAt('archive-uploaded')) {
    if (purged()) return abortSignedOut();
    const path = photoObjectPath(record, 'archive', record.archive.mime);
    const result = await transport.uploadObject(path, record.archive.bytes, record.archive.mime);
    if (!result.ok) return await fail(result.failure);
    const persisted = await persist({ ...record, stage: 'archive-uploaded', lastFailure: null });
    if (!persisted)
      return purged() ? abortSignedOut() : await fail('could not persist delivery state');
    record = persisted;
  }

  // ---- confirm -------------------------------------------------------------
  if (stageAt('confirmed')) {
    if (purged()) return abortSignedOut();
    // serverPhotoId is guaranteed non-null from 'attached' onwards (the parser
    // refuses the contradiction), but the type does not know that.
    if (record.serverPhotoId === null) return await fail('missing server photo id');
    const result = await transport.confirmArchive(record.serverPhotoId);
    if (!result.ok) return await fail(result.failure);
    // THE SERVER'S ANSWER IS THE TRUTH. A confirmation that came back without
    // a timestamp is not a confirmation, whatever the transport claimed.
    if (result.value.archiveUploadedAt === null) {
      return await fail('server did not confirm the archive');
    }
    const persisted = await persist({ ...record, stage: 'confirmed', lastFailure: null });
    if (!persisted)
      return purged() ? abortSignedOut() : await fail('could not persist delivery state');
    record = persisted;
  }

  // ---- cleanup — ONLY after confirmed server truth -------------------------
  // If this removal fails the record stays at 'confirmed' and a later pass
  // retries the removal alone; confirmation's idempotency makes that safe and
  // the server truth is never corrupted by a local cleanup problem.
  await staging.removeStaged(record.ownerUserId, record.clientPhotoUuid);
  return {
    clientPhotoUuid: record.clientPhotoUuid,
    stage: 'confirmed',
    delivered: true,
    failure: null,
  };
}

/** True while a delivery pass is running for an owner — the sequential guard. */
const inFlight = new Set<string>();

/**
 * An observer of queue progress. **Reporting only — it decides nothing.**
 *
 * Added in S05-04 so the screen can say "Uploading" about the photo the queue
 * is ACTUALLY working on, rather than re-deriving the queue's order in the UI
 * and hoping the two agree. The sequential guarantee means at most one photo
 * per owner is ever the subject of this call.
 *
 * It is deliberately not a progress percentage: a one-shot upload (D-025) has
 * no honest intermediate figure, and inventing one would be exactly the kind
 * of comforting fiction PT §20 rules out.
 */
export interface DeliveryObserver {
  readonly onPhotoStart?: (clientPhotoUuid: string) => void;
  /** The pass has ended — nothing is in flight. */
  readonly onIdle?: () => void;
}

/**
 * Deliver everything pending for an owner, ONE PHOTO AT A TIME, oldest first.
 * Reconstructs the queue from the durable store — never from memory — so
 * calling it after a relaunch resumes exactly where the last process died.
 *
 * A second call while one is running returns immediately with `alreadyRunning`
 * rather than interleaving uploads.
 */
export async function deliverPendingPhotos(
  staging: PhotoStagingStore,
  transport: PhotoTransportPort,
  ownerUserId: string,
  observer?: DeliveryObserver,
): Promise<{ alreadyRunning: boolean; outcomes: DeliveryOutcome[] }> {
  if (inFlight.has(ownerUserId)) return { alreadyRunning: true, outcomes: [] };
  inFlight.add(ownerUserId);
  try {
    // R1 B-1: the pass runs under the purge generation it began with. If the
    // owner signs out mid-pass, every later persist is refused by the store's
    // epoch guard and the loop below stops — the pass ends truthfully instead
    // of resurrecting purged records.
    const epoch = staging.purgeEpoch(ownerUserId);
    const outcomes: DeliveryOutcome[] = [];
    // The list is the durable truth — including records already 'confirmed'
    // whose cleanup failed last time; advancePhoto re-runs just the removal.
    const queue = await staging.list(ownerUserId);
    for (const record of queue) {
      // Sign-out landed mid-pass: everything still queued was just purged.
      // Stop rather than upload records that no longer exist.
      if (staging.purgeEpoch(ownerUserId) !== epoch) break;
      observer?.onPhotoStart?.(record.clientPhotoUuid);
      try {
        outcomes.push(await advancePhoto(staging, transport, record, epoch));
      } catch (error) {
        // A transport that THROWS (rather than returning a failure) must not
        // strand the photos behind it — one photo's crash is that photo's
        // problem. Its own stage is safe: every completed stage was persisted
        // before the call that threw.
        outcomes.push({
          clientPhotoUuid: record.clientPhotoUuid,
          stage: record.stage,
          delivered: false,
          failure: error instanceof Error ? error.message : String(error),
        });
      }
    }
    return { alreadyRunning: false, outcomes };
  } finally {
    inFlight.delete(ownerUserId);
    // In `finally` so a throw anywhere above cannot leave the screen saying a
    // photo is uploading forever.
    observer?.onIdle?.();
  }
}

/**
 * R1 F-1: admissions are SERIALIZED per owner. `stageForDelivery` reads the
 * server count and the staged list, evaluates the limits, and only then
 * persists — three awaits apart. Two concurrent admissions could both observe
 * the same pre-admission state and both pass a limit only one of them fits
 * under (11 photos + two calls → 13). The chain below makes the second
 * admission start only after the first has fully finished, so it sees the
 * first one's staged record and refuses correctly. Same shape as the
 * `inFlight` guard above — the smallest existing pattern — except admissions
 * QUEUE rather than bounce, because refusing an admission is a limits
 * decision, not a "try again later".
 */
const admissionChains = new Map<string, Promise<unknown>>();

async function withAdmissionLock<T>(ownerUserId: string, task: () => Promise<T>): Promise<T> {
  const previous = admissionChains.get(ownerUserId) ?? Promise.resolve();
  const run = previous.then(task, task);
  // The stored tail never rejects, so a refused admission cannot poison the
  // chain for the next one.
  admissionChains.set(
    ownerUserId,
    run.then(
      () => undefined,
      () => undefined,
    ),
  );
  return await run;
}

/**
 * Stage a verified photo AND enforce D-026 before anything is persisted.
 * The only entrance to the queue.
 */
export async function stageForDelivery(input: {
  readonly staging: PhotoStagingStore;
  readonly ownerUserId: string;
  readonly captureId: string;
  readonly photoClass: 'before' | 'after';
  readonly photo: import('./photo-pipeline').VerifiedPhoto;
  readonly maxSourceBytes: number;
  readonly nowMs: number;
}): Promise<
  | { ok: true; staged: StagedPhoto }
  | LimitRefusal
  | { ok: false; code: 'staging-failed'; reason: string }
> {
  const { staging, ownerUserId, captureId, photoClass, photo, maxSourceBytes, nowMs } = input;

  // F-1: the whole admission — count, list, limits, persist — runs alone.
  return await withAdmissionLock(ownerUserId, async () => {
    // B-1: an admission that began before a sign-out must not stage either.
    const epoch = staging.purgeEpoch(ownerUserId);

    // **NO SERVER READ STANDS IN FRONT OF STAGING (H-4 correction).** This
    // used to fetch the Capture's server photo count, because D-026's working
    // count needed it, and it refused the photo when that read failed. H-4
    // removed the count ceiling — `evaluatePhotoLimits` stopped reading the
    // number — but the request outlived the rule it served, so losing signal
    // still blocked a photo from reaching durable local storage on the
    // evidence of a limit that no longer exists.
    //
    // Both remaining safeguards are LOCAL and are unaffected: the 25 MB
    // per-photo ceiling and the 100 MB retained-byte budget, the second
    // computed from the staged list below. Nothing here makes the app
    // offline-first; it removes a dead prerequisite from a staging path that
    // was always durable, and the delivery queue already retries from that
    // durable store whenever connectivity returns.
    const staged = await staging.list(ownerUserId);
    const limits = evaluatePhotoLimits({
      staged,
      captureId,
      candidateSourceBytes: photo.metadata.sourceByteSize,
      maxSourceBytes,
    });
    if (!limits.ok) return limits;

    // Sign-out landed while the checks above awaited: staging now would write
    // a fresh record into a purged store. Refuse. (The store's own epoch
    // guard also covers a purge landing INSIDE stagePhoto's write.)
    if (staging.purgeEpoch(ownerUserId) !== epoch) {
      return {
        ok: false as const,
        code: 'staging-failed' as const,
        reason: 'Signed out before the photo could be kept.',
      };
    }

    const record = await staging.stagePhoto(ownerUserId, captureId, photoClass, photo, nowMs);
    if (record === null) {
      return {
        ok: false as const,
        code: 'staging-failed' as const,
        reason: 'This photo could not be kept on the device.',
      };
    }
    return { ok: true as const, staged: record };
  });
}
